/**
 * Application Entry Point
 *
 * This is the main entry point for the Vite build system.
 * It renders the Chakra UI version of the Plaid Quickstart app,
 * wrapped with all necessary providers for theming and state management.
 *
 * Provider Hierarchy:
 * 1. React.StrictMode - Development checks and warnings
 * 2. AppChakraProvider - Chakra UI theming and color mode
 * 3. QuickstartProvider - Application state management
 * 4. ChakraApp - Main application component with AppShell layout
 *
 * @fileoverview Vite-specific application entry point with Chakra UI
 */

import React from 'react';
import ReactDOM from 'react-dom/client';

/**
 * Chakra UI Provider
 *
 * Provides theme context and color mode support to all child components.
 * Includes custom theme with brand colors, typography, and component overrides.
 */
import { AppChakraProvider } from './providers/ChakraProvider';

/**
 * Quickstart State Provider
 *
 * Provides application state management using React Context + useReducer.
 * Manages link tokens, access tokens, product configuration, etc.
 */
import { QuickstartProvider } from './Context';

/**
 * Application Router
 *
 * The AppRouter component provides routing for the entire application:
 * - Home page with authentication (login/signup tabs)
 * - Protected routes requiring authentication
 * - Public routes for password recovery
 * - Main app accessible at /home route (protected)
 */
import { AppRouter } from './router';

/**
 * Error Boundary
 *
 * Catches uncaught render errors so a bug in one component shows a friendly
 * "Something went wrong" screen instead of unmounting the entire app to a
 * blank page. See components/ErrorBoundary.tsx for what it does and doesn't
 * cover.
 */
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * Root Element
 *
 * Get the root DOM element where React will mount the application.
 * This matches the div#root in index.html (Vite entry HTML)
 */
const rootElement = document.getElementById('root');

/**
 * Error Handling for Missing Root
 *
 * Throw an error if the root element is not found.
 * This helps debug issues with the HTML template.
 */
if (!rootElement) {
  throw new Error(
    'Root element not found. Make sure index.html has a div with id="root"'
  );
}

/**
 * Create React Root and Render
 *
 * Using React 18's createRoot API for concurrent features.
 * The component tree is wrapped with:
 *
 * - StrictMode: Enables additional development checks
 *   - Warns about deprecated lifecycle methods
 *   - Warns about legacy string refs
 *   - Detects unexpected side effects
 *
 * - AppChakraProvider: Enables Chakra UI features
 *   - Custom theme with brand colors
 *   - Dark/light mode switching
 *   - Color mode persistence in localStorage
 *   - Access to all Chakra UI components and hooks
 *
 * - QuickstartProvider: Enables application state
 *   - Link token management
 *   - Access token management
 *   - Product configuration
 *   - Error state handling
 */
const root = ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <AppChakraProvider>
      {/* Inside AppChakraProvider so the fallback screen still gets themed —
          but outside QuickstartProvider/AppRouter, so a crash in either of
          those is caught too. */}
      <ErrorBoundary>
        <QuickstartProvider>
          <AppRouter />
        </QuickstartProvider>
      </ErrorBoundary>
    </AppChakraProvider>
  </React.StrictMode>
);
